/**
 * Toast.tsx — minimal toast notifications (errors, warnings, info).
 *
 * Used for transient session feedback: connection errors, reconnect notices,
 * file-transfer failures, etc. (brief: "error toasts"). No dependency; a small
 * context + a fixed-position stack. Toasts auto-dismiss unless `sticky`.
 */

import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';

export type ToastKind = 'error' | 'warn' | 'info' | 'success';

export interface Toast {
  id: string;
  kind: ToastKind;
  message: string;
}

interface ToastApi {
  push: (kind: ToastKind, message: string, opts?: { ttlMs?: number }) => string;
  dismiss: (id: string) => void;
  error: (message: string, opts?: { ttlMs?: number }) => string;
  warn: (message: string, opts?: { ttlMs?: number }) => string;
  info: (message: string, opts?: { ttlMs?: number }) => string;
  success: (message: string, opts?: { ttlMs?: number }) => string;
}

const ToastContext = createContext<ToastApi | null>(null);

const DEFAULT_TTL = 6000;

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const timers = useRef(new Map<string, ReturnType<typeof setTimeout>>());

  const dismiss = useCallback((id: string) => {
    setToasts((ts) => ts.filter((t) => t.id !== id));
    const timer = timers.current.get(id);
    if (timer) {
      clearTimeout(timer);
      timers.current.delete(id);
    }
  }, []);

  const push = useCallback(
    (kind: ToastKind, message: string, opts?: { ttlMs?: number }) => {
      const id = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
      setToasts((ts) => [...ts, { id, kind, message }]);
      const ttl = opts?.ttlMs ?? DEFAULT_TTL;
      if (ttl > 0) {
        timers.current.set(
          id,
          setTimeout(() => dismiss(id), ttl),
        );
      }
      return id;
    },
    [dismiss],
  );

  const api = useMemo<ToastApi>(
    () => ({
      push,
      dismiss,
      error: (m, o) => push('error', m, o),
      warn: (m, o) => push('warn', m, o),
      info: (m, o) => push('info', m, o),
      success: (m, o) => push('success', m, o),
    }),
    [push, dismiss],
  );

  return (
    <ToastContext.Provider value={api}>
      {children}
      <ToastStack toasts={toasts} onDismiss={dismiss} />
    </ToastContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components -- hook lives with its provider by design
export function useToast(): ToastApi {
  const api = useContext(ToastContext);
  if (!api) throw new Error('useToast must be used within <ToastProvider>');
  return api;
}

function ToastStack({ toasts, onDismiss }: { toasts: Toast[]; onDismiss: (id: string) => void }) {
  return (
    <div
      className="pointer-events-none fixed bottom-4 right-4 z-50 flex w-80 flex-col gap-2"
      role="region"
      aria-label="Notifications"
    >
      {toasts.map((t) => (
        <div
          key={t.id}
          role={t.kind === 'error' ? 'alert' : 'status'}
          className={`pointer-events-auto flex items-start gap-2 rounded-md border px-3 py-2 text-sm shadow-lg ${classes(t.kind)}`}
        >
          <span className="flex-1">{t.message}</span>
          <button
            type="button"
            aria-label="Dismiss"
            onClick={() => onDismiss(t.id)}
            className="ml-1 shrink-0 rounded px-1 text-xs opacity-70 hover:opacity-100"
          >
            ✕
          </button>
        </div>
      ))}
    </div>
  );
}

function classes(kind: ToastKind): string {
  switch (kind) {
    case 'error':
      return 'border-helm-bad/50 bg-helm-bad/10 text-helm-bad';
    case 'warn':
      return 'border-helm-warn/50 bg-helm-warn/10 text-helm-warn';
    case 'success':
      return 'border-helm-good/50 bg-helm-good/10 text-helm-good';
    case 'info':
    default:
      return 'border-helm-border bg-helm-panel text-helm-text';
  }
}
